<template>
	<div class="grid-item pilot-identity mech-identity">
		<div class="header">
			<div class="col grow-max">
				<div class="heading h1">{{ mech.name || "UNREGISTERED" }}</div>
				<div class="heading h2">
					({{ hasMech ? `${mech.manufacturer} ${mech.frameName}` : "NO FRAME ON FILE" }})
				</div>
			</div>
			<div class="col header-actions">
				<button
					v-if="embedded && hasMech"
					class="foundry-open"
					v-tip="{ title: 'Open in Foundry', body: 'Open this mech\'s sheet in Foundry.' }"
					@click.stop="openInFoundry('mech', pilot.callsign)"
				>
					<i class="mdi mdi-open-in-new" aria-hidden="true"></i>
				</button>
				<img
					v-if="mech.manufacturer"
					:src="manufacturerLogo"
					@error="e => (e.target.style.display = 'none')"
				/>
			</div>
		</div>
		<div class="body">
			<div class="add-padding ident-record">
				Union Administrative RM-6 Mechanical Blueprint Registration (CHASSIS) Record
				{{ mech.id || "[NO RECORD]" }}
			</div>
			<div class="flex-container-rows">
				<div class="row flex-container-cols add-padding">
					<div class="col grow-max flex-container-rows" style="padding-top: 5px">
						<div class="row flex-container-cols">
							<div class="col col-primary">
								<span class="flavor-text">
									Pilot: <b class="accent--text">{{ pilot.callsign }}</b
									><br />
									Frame:
									<b v-if="hasMech" class="accent--text"
										>{{ mech.manufacturer }} {{ mech.frameName }}</b
									><b v-else class="unregistered">[UNREGISTERED]</b><br />
									Role: <b v-if="hasMech" class="accent--text">{{ mech.mechtype || "—" }}</b
									><b v-else class="unregistered">[UNREGISTERED]</b></span
								>
							</div>
							<div class="col">
								<div
									v-for="stamp in chassisStamps"
									:key="stamp.text"
									:class="stamp.ok ? 'verified' : 'unverified'"
								>
									{{ stamp.text }}
								</div>
							</div>
						</div>
						<div class="ident-section">
							FRAME BASELINE PERFORMANCE <span class="subtle--text">(PRE-PILOT CALIBRATION)</span>
						</div>
						<div class="row stat-grid" style="padding-top: 5px">
							<span v-for="stat in stats" :key="stat.label"
								>{{ stat.label }}:
								<span class="stat-text accent--text">{{ stat.value }}</span></span
							>
						</div>
						<template v-if="condition.length">
							<div class="ident-section">
								CURRENT CONDITION <span class="subtle--text">(LIVE FROM FOUNDRY)</span>
							</div>
							<div class="row stat-grid" style="padding-top: 5px">
								<span v-for="stat in condition" :key="stat.label"
									>{{ stat.label }}:
									<span class="stat-text" :class="stat.alert ? 'alert--text' : 'accent--text'">{{
										stat.value
									}}</span></span
								>
							</div>
						</template>
						<div class="row flex-container-cols ident-section">
							<div class="col col-share">
								<span>ARMAMENT AUDIT</span>
								<br />
								<div class="chip-container" v-for="(weapon, i) in mech.weapons" :key="i">
									<span class="chip" v-tip="tip('weapon', weapon)"
										><i aria-hidden="true" class="notranslate cci cci-weapon"></i>{{ weapon.name
										}}<span v-if="weapon.mount" class="subtle--text">
											// {{ weapon.mount }}</span
										></span
									>
								</div>
								<div v-if="!mech.weapons.length" class="subtle--text">ERR: NO ARMAMENT</div>
							</div>
							<div class="col col-share">
								<span>SYSTEMS AUDIT</span>
								<br />
								<div class="chip-container" v-for="(system, i) in mech.systems" :key="i">
									<span class="chip" v-tip="tip('system', system)"
										><i aria-hidden="true" class="notranslate cci cci-system"></i
										>{{ system.name }}</span
									>
								</div>
								<div v-if="!mech.systems.length" class="subtle--text">ERR: NO SYSTEMS</div>
							</div>
						</div>
						<div class="row flex-container-cols">
							<div class="col" style="padding-top: 5px">
								<span
									>FRAME TRAITS<span v-if="mech.core">
										// CORE: <span v-tip="lookup.core(mech.core)">{{ mech.core }}</span></span
									></span
								>
								<br />
								<div class="chip-container" v-for="trait in mech.traits" :key="trait">
									<span class="chip" v-tip="lookup.trait(trait)"
										><i aria-hidden="true" class="notranslate cci cci-trait"></i>{{ trait }}</span
									>
								</div>
							</div>
						</div>
					</div>
					<div class="col">
						<div class="pilot-image-container">
							<div class="pilot-image-border">
								<img
									:src="mech.art"
									class="portrait"
									@error="e => (e.target.src = '/nodata-mech.png')"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<!-- Link back to the pilot: a full-width, right-aligned row like the pilot
			     card's blueprint box, so long names stay on one line -->
			<div class="pilot-link-row">
				<div class="pilot-link" @click="openPilot">
					<div>
						PILOT IDENT RECORD<br />
						"{{ pilot.callsign.toUpperCase() }}" :: {{ pilot.name || "UNIDENTIFIED" }}
					</div>
					<i
						aria-hidden="true"
						class="v-icon notranslate mdi mdi-fingerprint theme--dark grey--text text--darken-2"
					></i>
				</div>
			</div>
			<hr role="separator" aria-orientation="horizontal" class="ma-2 v-divider theme--dark" />
			<div class="row row--dense add-padding frame-description" v-html="mech.description"></div>
		</div>
	</div>
</template>

<script>
import { lookup } from "@/utils/lancerInfo";
import { embedded, openInFoundry } from "@/data/liveState.js";

// Pilots without a mech still get a card, just an empty one
const NO_MECH = {
	id: "",
	name: "",
	manufacturer: "",
	frameName: "",
	mechtype: "",
	description: "",
	stats: {},
	traits: [],
	core: "",
	weapons: [],
	systems: [],
	art: "/nodata-mech.png",
};

export default {
	props: {
		pilot: {
			type: Object,
			required: true,
		},
	},
	data() {
		return { lookup };
	},
	computed: {
		// Inside Foundry the card offers to open the sheet there
		embedded() {
			return embedded;
		},
		// HP, structure, stress, heat... as Foundry has them now (src/data/liveState.js); none from the files
		condition() {
			const c = this.mech.condition;
			if (!c) return [];
			const of = track => (track ? `${track.value}/${track.max}` : "—");
			return [
				{ label: "HP", value: of(c.hp), alert: c.hp && c.hp.value <= c.hp.max / 4 },
				{ label: "STRUCT", value: of(c.structure), alert: c.structure && c.structure.value <= 1 },
				{ label: "STRESS", value: of(c.stress), alert: c.stress && c.stress.value <= 1 },
				{ label: "HEAT", value: of(c.heat), alert: c.heat && c.heat.value >= c.heat.max },
				{ label: "OVERSHIELD", value: c.overshield ?? 0 },
				{ label: "BURN", value: c.burn ?? 0, alert: c.burn > 0 },
				{ label: "REPAIRS", value: of(c.repairs) },
				{ label: "CORE", value: c.coreEnergy ? "READY" : "SPENT" },
			];
		},
		mech() {
			return this.pilot.mech || NO_MECH;
		},
		hasMech() {
			return !!this.pilot.mech;
		},
		// GMS frames need no license; others need their license at rank II. An
		// alternate frame (e.g. Enkidu) is covered by its base frame's license (Tokugawa).
		frameLicensed() {
			if (!this.hasMech) return false;
			if (String(this.mech.manufacturer).toUpperCase() === "GMS") return true;
			const frames = (this.mech.licenseFrames || [this.mech.frameName]).map(f =>
				String(f).toLowerCase()
			);
			return this.pilot.licenses.some(license => {
				const match = String(license).match(/^(.*?)\s+(I{1,3})$/);
				const name = (match ? match[1] : String(license)).toLowerCase();
				const rank = match ? match[2].length : 0;
				return rank >= 2 && frames.some(frame => name === frame || name.endsWith(` ${frame}`));
			});
		},
		chassisStamps() {
			if (!this.hasMech) {
				return [
					{ ok: false, text: "FRAME LICENSE INVALID" },
					{ ok: false, text: "CHASSIS UNAVAILABLE" },
					{ ok: false, text: "REACTOR OFFLINE" },
				];
			}
			return [
				this.frameLicensed
					? { ok: true, text: "FRAME LICENSE VALID" }
					: { ok: false, text: "FRAME LICENSE INVALID" },
				{ ok: true, text: "CHASSIS INSPECTED" },
				{ ok: true, text: "REACTOR STABLE" },
			];
		},
		manufacturerLogo() {
			// Manufacturer code -> logo file in public/faction-logos (most are the code itself)
			const file =
				{ IC: "intercorp" }[this.mech.manufacturer] || this.mech.manufacturer.toLowerCase();
			return `/faction-logos/${file}.svg`;
		},
		stats() {
			const s = this.mech.stats;
			return [
				["SIZE", s.size],
				["HP", s.hp],
				["ARMOR", s.armor],
				["EVA", s.evasion],
				["E-DEF", s.edef],
				["HEAT", s.heatcap],
				["SPD", s.speed],
				["SENS", s.sensor_range],
				["SAVE", s.save],
				["SP", s.sp],
			].map(([label, value]) => ({ label, value: value ?? "—" }));
		},
	},
	methods: {
		openInFoundry,
		// Data lookup first; a description written in the pilot file covers anything the data lacks
		tip(kind, item) {
			return (
				lookup[kind](item.name) ||
				(item.description ? { title: item.name, body: item.description } : null)
			);
		},
		openPilot() {
			this.$router.push({ path: "/pilots", query: { pilot: this.pilot.callsign } });
		},
	},
};
</script>

<style scoped>
.stat-grid {
	display: flex;
	flex-wrap: wrap;
	column-gap: 1em;
	font-size: 18px;
}

.stat-grid .stat-text {
	font-size: 20px;
}

.frame-description {
	flex-shrink: 0;
	max-height: 9.75em;
	overflow-y: auto;
	font-size: 13px;
	line-height: 1.5em;
	opacity: 0.7;
}

/* Link back to the pilot, tucked under the portrait like the pilot card's blueprint box */
.pilot-link-row {
	display: flex;
	justify-content: flex-end;
	padding: 0.4em 0.5em 0;
}

.pilot-link {
	display: flex;
	align-items: center;
	gap: 0.6em;
	padding: 0.25em 0.5em;
	text-align: right;
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 0.2s ease-in-out;
}

.pilot-link:hover {
	background-color: var(--highlight-hover);
}

.pilot-link .mdi {
	font-size: 36px;
	line-height: 1;
}

.frame-description :deep(p) {
	margin: 0.5em 0;
}
</style>
